import { Inline, Paragraph } from 'canoe';
import { useFormikContext } from 'formik';
import get from 'lodash/get';
import has from 'lodash/has';
import React, { Fragment, useEffect, useState } from 'react';

import { AutocompleteSelect, DefaultItemProps, Select } from '@candu-ai/analytics/ui';
import {
  isRelativeDate,
  LogicRulesNames,
  notLogicRule,
  TraitTypes,
} from '@candu-ai/segments/types';
import { useUserTraits } from 'craft/hooks/useUserTraits';
import { FormikFieldSetter } from 'craft/types';
import { stripNameDifferentiator } from 'dashboard/pages/Segment/components/DefinitionForm/nameDifferentiatorUtils';
import {
  DefinitionDatePicker,
  DefinitionInput,
} from 'dashboard/pages/Segment/components/DefinitionFormFields';
import { SelectInputWrapper } from 'dashboard/pages/Segment/components/DefinitionFormFields/DefinitionFormComponents';
import {
  SetRuleSummary,
  UploadButton,
} from 'dashboard/pages/Segment/components/Rule/SetContainsRule';
import {
  autoCompleteStringOperands,
  booleanOperations,
  booleanOptions,
  datesOperations,
  isDefinedOptions,
  isStringDefinedOptions,
  multiselectStringOperands,
  numberOperations,
  stringOperations,
} from 'dashboard/pages/Segment/components/constants';
import { DefinitionFormikValues } from 'dashboard/pages/Segment/components/types';

import { TraitSelect } from './TraitSelect';

const TraitTypeOperationMappings = {
  [TraitTypes.String]: stringOperations,
  [TraitTypes.Boolean]: booleanOperations,
  [TraitTypes.Number]: numberOperations,
  [TraitTypes.Date]: datesOperations,
};

export type TraitQueryItem = {
  name: string;
  source: string;
  traitType: TraitTypes;
};

export type TraitQueryData = TraitQueryItem[];

interface renderUserPropertyOperationFieldsProps {
  values: DefinitionFormikValues;
  setFieldValue: FormikFieldSetter;
  base: string;
  traitName: string;
  traitType: TraitTypes;
  readOnly?: boolean;
}

const renderUserPropertyOperationFields = ({
  base,
  traitName,
  traitType,
  readOnly,
  values,
  setFieldValue,
}: renderUserPropertyOperationFieldsProps) => {
  const placeholder = 'Choose operator';

  const operationSetPath = `${base}.UserProperty.operation.setId`;
  const operationOperandPath = `${base}.UserProperty.operation.operand`;
  const operationTypePath = `${base}.UserProperty.operation.type`;

  const apiQuery = (search: string) => ({
    query: {
      finder: 'byUserTraitValue',
      field: traitName,
      q: `${search.trim()}`,
    },
  });

  const getFormattedResults = (results: string[]) => {
    return (
      results.map((result) => {
        return { label: result, value: result };
      }) || []
    );
  };

  const operationType = get(values, operationTypePath, null);
  const setId = get(values, operationSetPath);

  const shouldShow =
    operationType && !isDefinedOptions.some((option) => option.value === operationType);

  if (traitType === TraitTypes.Number) {
    return (
      <>
        <Select<DefaultItemProps, DefinitionFormikValues>
          name={operationTypePath}
          options={numberOperations}
          readOnly={readOnly}
          placeholder={placeholder}
          InputWrapper={SelectInputWrapper}
          data-cy="segment-definition-selector"
          values={values}
          setFieldValue={setFieldValue}
          validateOnSelectWithErrors
        />
        {shouldShow && (
          <DefinitionInput type="number" name={operationOperandPath} readOnly={readOnly} />
        )}
      </>
    );
  }

  if (traitType === TraitTypes.Date) {
    const isAbsoluteDate = !isRelativeDate(operationType);
    return (
      <>
        <Select<DefaultItemProps, DefinitionFormikValues>
          name={operationTypePath}
          options={datesOperations}
          readOnly={readOnly}
          placeholder={placeholder}
          InputWrapper={SelectInputWrapper}
          data-cy="segment-definition-selector"
          values={values}
          setFieldValue={setFieldValue}
          validateOnSelectWithErrors
        />
        {shouldShow && operationType && isAbsoluteDate && (
          <DefinitionDatePicker name={operationOperandPath} readOnly={readOnly} />
        )}
        {shouldShow && operationType && !isAbsoluteDate && (
          <Inline align="center" space="xs">
            <DefinitionInput name={operationOperandPath} type="number" readOnly={readOnly} />
            <Paragraph noMargin>days</Paragraph>
          </Inline>
        )}
      </>
    );
  }

  if (traitType === TraitTypes.Boolean) {
    return (
      <>
        <Select<DefaultItemProps, DefinitionFormikValues>
          name={operationTypePath}
          options={booleanOperations}
          readOnly={readOnly}
          placeholder={placeholder}
          InputWrapper={SelectInputWrapper}
          data-cy="segment-definition-selector"
          values={values}
          setFieldValue={setFieldValue}
          validateOnSelectWithErrors
        />
        {shouldShow && (
          <Select<DefaultItemProps, DefinitionFormikValues>
            name={operationOperandPath}
            options={booleanOptions}
            readOnly={readOnly}
            InputWrapper={SelectInputWrapper}
            data-cy="segment-definition-selector"
            values={values}
            setFieldValue={setFieldValue}
            validateOnSelectWithErrors
          />
        )}
      </>
    );
  }

  const shouldShowMultiselect = multiselectStringOperands.includes(operationType);

  const shouldShowString =
    operationType && !isStringDefinedOptions.some((option) => option.value === operationType);

  const shouldShowFreeFormInput = !autoCompleteStringOperands.includes(operationType);

  return (
    <>
      <Select<DefaultItemProps, DefinitionFormikValues>
        name={operationTypePath}
        options={stringOperations}
        readOnly={readOnly}
        placeholder={placeholder}
        InputWrapper={SelectInputWrapper}
        data-cy="segment-definition-selector"
        values={values}
        setFieldValue={setFieldValue}
        validateOnSelectWithErrors
      />

      {setId ? (
        <SetRuleSummary base={`${base}.UserProperty`} setId={setId} setFieldValue={setFieldValue} />
      ) : null}

      {!setId && traitName && shouldShowString && !shouldShowFreeFormInput ? (
        <AutocompleteSelect<DefaultItemProps, string[], DefinitionFormikValues>
          name={operationOperandPath}
          apiQuery={apiQuery}
          readOnly={readOnly}
          getFormattedResults={getFormattedResults}
          isMultiselect={shouldShowMultiselect}
          allowCustomValues
          InputWrapper={SelectInputWrapper}
          data-cy="segment-definition-selector"
          values={values}
          setFieldValue={setFieldValue}
        />
      ) : null}

      {!setId && traitName && shouldShowString && shouldShowFreeFormInput ? (
        <DefinitionInput type="text" name={operationOperandPath} readOnly={readOnly} />
      ) : null}

      {(operationType === LogicRulesNames.Contains ||
        operationType === notLogicRule(LogicRulesNames.Contains)) && (
        <UploadButton
          base={`${base}.UserProperty`}
          setFieldValue={setFieldValue}
          operationType={operationType}
        />
      )}
    </>
  );
};

export interface UserPropertyRuleProps {
  base: string;
  readOnly?: boolean;
}

// check if the existing operation type is compatible with the newly selected trait, returns '' otherwise
const checkTraitChangeCompatibility = (traitType: TraitTypes, operation: string) => {
  if (traitType === TraitTypes.Boolean) {
    return booleanOperations.find((op) => op.value === operation)?.value || '';
  }
  if (traitType === TraitTypes.String) {
    return stringOperations.find((op) => op.value === operation)?.value || '';
  }
  if (traitType === TraitTypes.Number) {
    return numberOperations.find((op) => op.value === operation)?.value || '';
  }
  if (traitType === TraitTypes.Date) {
    return datesOperations.find((op) => op.value === operation)?.value || '';
  }
  return '';
};

export const UserPropertyRule = ({ base, readOnly }: UserPropertyRuleProps) => {
  const { values, setFieldValue } = useFormikContext<DefinitionFormikValues>();

  const traitNamePath = `${base}.traitName`;
  const traitTypePath = `${base}.UserProperty.traitType`;
  const operationOperandPath = `${base}.UserProperty.operation.operand`;
  const operationTypePath = `${base}.UserProperty.operation.type`;

  const [traitType, setTraitType] = useState(get(values, traitTypePath, TraitTypes.String));
  const [traitName, setTraitName] = useState(
    stripNameDifferentiator(get(values, traitNamePath, null) || ''),
  );

  const { unfilteredData: userTraits, loading } = useUserTraits();

  const value = get(values, traitNamePath);

  const matchTraitType = () => {
    if (!userTraits || !userTraits.length || !value) {
      return;
    }

    const splitValue = value.split('_differentiator(');
    const name = splitValue[0];
    const type = splitValue[1] ? splitValue[1].replace(')', '') : undefined;

    // get the item with exact name and type
    const traitItem =
      userTraits.find((trait) => {
        return trait.name === name && (!type || trait.traitType === type);
      }) || userTraits.filter((trait) => trait.name === name);

    if (!traitItem || Array.isArray(traitItem)) {
      // TODO: we might want to handle multiple trait matches better in the future
      return;
    }

    // if trait exists in our data make sure that the parsed type is correct
    // if it doesn't match, then update it - especially important for Dates
    const typeFromData = traitItem.traitType;
    const savedType = get(values, traitTypePath);

    if (typeFromData && typeFromData !== savedType) {
      setTraitType(typeFromData);
      setFieldValue(traitTypePath, typeFromData, false);
    }
  };

  useEffect(() => {
    matchTraitType();
  }, [userTraits]);

  useEffect(() => {
    // When a trait type changes there can be an operation type mismatch
    // We check for common cases and translate if possible, otherwise we discard the value
    const operationType = get(values, operationTypePath, null);

    const isOperationTypeMismatch = !TraitTypeOperationMappings[traitType as TraitTypes].find(
      (type) => type.value === operationType,
    );

    if (isOperationTypeMismatch) {
      if (
        traitType === TraitTypes.String &&
        isDefinedOptions.find((type) => type.value === operationType)
      ) {
        // mismatch of Defined options for String, translate to correct value
        setFieldValue(
          operationTypePath,
          isStringDefinedOptions[isDefinedOptions.findIndex((type) => type.value === operationType)]
            .value,
          false,
        );
      } else if (
        traitType !== TraitTypes.String &&
        isStringDefinedOptions.find((type) => type.value === operationType)
      ) {
        // mismatch of Defined options for all other trait types, translate to correct value
        setFieldValue(
          operationTypePath,
          isDefinedOptions[isStringDefinedOptions.findIndex((type) => type.value === operationType)]
            .value,
          false,
        );
      } else {
        // mismatch of non-translatable operation type, clear value
        setFieldValue(operationTypePath, '', false);
      }
    }
  }, [values, operationTypePath, traitType]);

  const onQuerySuccess = (queryData: TraitQueryData) => {
    // if trait name doesn't exist in the query results, treat it as a custom value
    const isCustomTrait = !queryData.find(({ name }) => name === traitName);

    const firstValue = isCustomTrait
      ? { name: traitName, traitType: TraitTypes.String }
      : queryData[0];

    if (!has(values, traitNamePath) || isCustomTrait) {
      setTraitName(firstValue?.name || '');
    }
    if (!has(values, traitTypePath) || isCustomTrait) {
      setTraitType(firstValue?.traitType || TraitTypes.String);
      setFieldValue(traitTypePath, firstValue?.traitType || TraitTypes.String, false);
    }
  };

  return (
    <Fragment key={traitName}>
      <TraitSelect
        name={traitNamePath}
        readOnly={readOnly}
        handleChange={(selectedTrait: TraitQueryItem) => {
          const traitTypeCompatibility = checkTraitChangeCompatibility(
            selectedTrait.traitType,
            get(values, operationTypePath),
          );

          if (traitTypeCompatibility === '') {
            // only clear the operand if the type is not compatible
            setFieldValue(operationOperandPath, '', false);
          }
          setFieldValue(operationTypePath, traitTypeCompatibility, false);
          setTraitName(stripNameDifferentiator(selectedTrait.name));
          setTraitType(selectedTrait.traitType || TraitTypes.String);
          setFieldValue(traitTypePath, selectedTrait.traitType || TraitTypes.String, false);
        }}
        onQuerySuccess={onQuerySuccess}
      />
      {value &&
        value !== '' &&
        !loading &&
        renderUserPropertyOperationFields({
          base,
          readOnly,
          values,
          setFieldValue,
          traitName,
          traitType,
        })}
    </Fragment>
  );
};
